// Deterministic "is this article worth a Gemini call" filter — the browser's
// copy. The hourly scanner applies the same rule before any AI runs, so the
// client enrichment (GeoIntelTab → classifyNewArticles) no longer sends the
// whole general feed to Gemini: only what overlaps a held ticker or a held
// sector, exactly as the cron does.
//
// ─── Kept in step with netlify/functions/lib/geoCandidates.js ────────────────
// That file is the CommonJS twin for the functions; this is the ES module for
// the client. CRA's module scope forbids importing it from outside src/, and a
// function cannot import an ES module from src/, so the logic exists twice.
// geoCandidates.test.js loads both and asserts the tables and the filter's
// answers are identical — a change to one without the other fails the suite.

// English news keywords that signal a sector in the classifier's taxonomy.
// Each list mixes sector-identifying terms with the geopolitical terms that
// typically hit that sector, so a macro event (tariff / sanction / export
// control) is kept only when it lands on a sector someone actually holds.
const SECTOR_KEYWORDS = {
  "שבבים":         ["semiconductor", "chipmaker", "chip maker", "chip export", "chip ban", "gpu", "foundry", "nvidia", "tsmc", "asml", "export control"],
  "אנרגיה":        ["oil", "opec", "crude", "natural gas", "pipeline", "refinery", "barrel", "petroleum", "embargo"],
  "רכב":           ["automaker", "electric vehicle", "ev ", "carmaker", "vehicle tariff", "auto tariff"],
  "תעשייה":        ["manufacturing", "industrial", "factory", "machinery", "steel tariff", "aerospace"],
  "קמעונות/מזון":  ["retailer", "grocery", "consumer goods", "food price"],
  "ייצור טכנולוגי":["hardware", "electronics", "pc maker", "server maker", "data center"],
  "רשתות חברתיות": ["social media", "tiktok ban", "meta platforms", "content moderation"],
  "מחשוב קוואנטי": ["quantum computing", "quantum chip"],
  "סייבר":         ["cyberattack", "hacker", "data breach", "ransomware", "malware", "cyber"],
  "ביטחון/חלל":    ["defense", "military", "missile", "weapon", "warfare", "armed conflict", "satellite", "space launch"],
  "פארמה":         ["pharma", "drug", "fda", "vaccine", "biotech"],
  // Two taxonomy sectors the previous table had no keywords for at all, so a
  // bank or a shipper in the portfolio never let a sanctions or shipping
  // article through.
  "פיננסים":       ["bank", "banking", "lender", "insurer", "financial sector", "interest rate", "federal reserve", "swift"],
  "שילוח וסחר":    ["shipping", "freight", "container", "port of", "canal", "strait", "tariff", "customs", "supply chain"],
};

// Finnhub `finnhubIndustry` → sector key. A copy of INDUSTRY_RULES in
// src/utils/sectorClassify.js (see the header there for why the order is what
// it is). Ordered — first match wins.
const INDUSTRY_RULES = [
  ["MEDIA",    /media|telecom|communication|broadcast|entertainment|publish/],
  ["HEALTH",   /pharma|biotech|biochem|medical|medicine|health|life science/],
  ["TECH",     /semiconductor|technology|software|hardware|computer|internet|electronic/],
  ["CONSUMER", /retail|consumer|food|beverage|restaurant|apparel|hotel|household|textile|luxury|leisure|tobacco/],
  ["AUTO",     /\bauto(mobile|motive)?s?\b|\bvehicles?\b/],
  ["FINANCE",  /financ|bank|insur/],
  ["ENERGY",   /energy|oil|gas|petrol|utilities|coal/],
  ["DEFENSE",  /aerospace|defense|defence/],
  // Materials in GICS, ahead of INDUSTRIAL's "construction".
  ["MATERIALS", /construction materials/],
  ["INDUSTRIAL", /machinery|industrial|construction|engineering|building|transportation|logistics|airline|marine|commercial services|electrical equipment|professional services|road & rail|railroad|trading compan/],
  ["CONSUMER", /^distributors$/],
  ["REALESTATE", /real estate|reit/],
  // Materials in GICS: Chemicals, Metals & Mining, Packaging, Paper & Forest
  // (sprint 16, David). Last, on purpose: "Coal Mining" goes to ENERGY
  // and "Construction Materials" is caught by its own rule above.
  ["MATERIALS", /chemical|metals|mining|steel|alumin|packag|paper|forest/],
];

// Sector key → the classifier's sectors it can stand for. Finnhub's taxonomy
// stops at "Technology", so a technology holding opens every technology theme
// in the geo taxonomy; the classifier then names the specific one.
const KEY_TO_GEO = {
  TECH:       ["שבבים", "ייצור טכנולוגי", "סייבר", "מחשוב קוואנטי"],
  MEDIA:      ["רשתות חברתיות"],
  HEALTH:     ["פארמה"],
  CONSUMER:   ["קמעונות/מזון"],
  AUTO:       ["רכב"],
  FINANCE:    ["פיננסים"],
  ENERGY:     ["אנרגיה"],
  DEFENSE:    ["ביטחון/חלל"],
  INDUSTRIAL: ["תעשייה", "שילוח וסחר"],
  REALESTATE: [],
  // The geopolitical taxonomy has no materials theme (geoEvents.js).
  MATERIALS:  [],
};

// The app's unified sector labels (what a holding's live `sector` field
// carries in the browser — sectorClassify's SECTOR_LABELS) → sector key.
const APP_LABEL_TO_KEY = {
  "תקשורת": "MEDIA", "בריאות": "HEALTH", "טכנולוגיה": "TECH", "רכב": "AUTO",
  "פיננסים": "FINANCE", "אנרגיה": "ENERGY", "צרכנות": "CONSUMER",
  "ביטחון/חלל": "DEFENSE", "תעשייה": "INDUSTRIAL", "נדל\"ן": "REALESTATE",
  "חומרי גלם": "MATERIALS",
};

function industryToKey(industry) {
  const s = String(industry || "").trim().toLowerCase();
  if (!s) return null;
  for (const [key, re] of INDUSTRY_RULES) if (re.test(s)) return key;
  return null;
}

// Geo-taxonomy sectors for a Finnhub industry string (server side).
function geoSectorsForIndustry(industry) {
  const key = industryToKey(industry);
  return key ? KEY_TO_GEO[key] || [] : [];
}

// Geo-taxonomy sectors for a holding's unified sector label (client side).
function geoSectorsForAppSector(label) {
  const key = APP_LABEL_TO_KEY[label];
  return key ? KEY_TO_GEO[key] || [] : [];
}

const escapeRe = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Keep an article iff it overlaps a held TICKER (Finnhub `related` tag, or a
// >=3-char ticker as a whole word in title+excerpt) or a held SECTOR (keyword
// in title+excerpt). Skips urls already classified. `tickers` and `sectors`
// are Sets (sectors in the geo taxonomy); `knownUrls` a Set of source_urls.
function selectCandidates(articles, tickers, sectors, knownUrls, cap = Infinity) {
  const out = [];
  const tickerList = [...tickers];
  const tickerRes = tickerList
    .filter(t => t.length >= 3)
    .map(t => new RegExp(`\\b${escapeRe(t)}\\b`));
  const sectorKeys = [...sectors].flatMap(sec => SECTOR_KEYWORDS[sec] || []);

  for (const a of articles) {
    if (!a || !a.url || knownUrls.has(a.url)) continue;
    const related = new Set(a.symbols || []);
    const hay = `${a.title || ""} ${a.excerpt || ""}`;
    const tickerHit = tickerList.some(t => related.has(t)) || tickerRes.some(re => re.test(hay));
    let sectorHit = false;
    if (!tickerHit && sectorKeys.length) {
      const low = hay.toLowerCase();
      sectorHit = sectorKeys.some(k => low.includes(k));
    }
    if (tickerHit || sectorHit) out.push(a);
    if (out.length >= cap) break;
  }
  return out;
}

export {
  SECTOR_KEYWORDS, INDUSTRY_RULES, KEY_TO_GEO, APP_LABEL_TO_KEY,
  industryToKey, geoSectorsForIndustry, geoSectorsForAppSector, selectCandidates,
};
